import type { DashboardSalesResponse } from '@aeris/contracts'
import { parseAmount } from '@aeris/finance'
import { MONEY_SCALE, formatMoney, sum, toString } from '@aeris/money'
import {
  tableReportPrintFor,
  type PrintDocument,
  type ReceiptTemplate,
} from '@aeris/printing'

import type { PrintedCompany } from './company-header.ts'

/**
 * O resumo do período no papel (F236).
 *
 * O gerente da Elite, em 05/10: "quero imprimir um relatório de quanto vendeu
 * de dinheiro, de cartão de débito, de Pix, quem vendeu… o prejuízo de troca
 * de x mil nesse mês, e também x mil de vale". O Painel já mostrava tudo isso
 * na tela, cada número num canto; faltava a folha que ele leva para conferir.
 *
 * Função pura, longe do React: o que entra no papel tem teste próprio, e a
 * tela só abre a prévia.
 */

const brl = (amount: string): string => formatMoney(parseAmount(amount))

/**
 * O que entrou de DINHEIRO no período (F219): a soma do "Recebido por
 * forma" sem o vale, que é a loja pagando a si mesma — não dinheiro novo.
 *
 * Mora aqui, e não no Painel, para a tela e o papel somarem do mesmo jeito.
 */
export const receivedCash = (
  methods: DashboardSalesResponse['receivedByMethod']
): { total: string; top: string } => {
  const cash = methods.filter((method) => method.kind !== 'voucher')
  return {
    total: toString(
      sum(
        cash.map((method) => parseAmount(method.amount)),
        MONEY_SCALE
      )
    ),
    top: cash
      .slice(0, 3)
      .map((method) => `${method.name} ${brl(method.amount)}`)
      .join(' · '),
  }
}

const plural = (count: number, one: string, many: string): string =>
  `${String(count)} ${count === 1 ? one : many}`

export const dashboardSummaryPrintFor = (input: {
  readonly data: DashboardSalesResponse
  readonly company: PrintedCompany
  readonly template: ReceiptTemplate
  readonly periodLabel: string
  readonly branchLabel: string
  /** Com uma filial escolhida: os vales são da loja inteira (F214). */
  readonly branchChosen: boolean
}): PrintDocument => {
  const { data } = input
  const { sales, exchanges } = data
  const cash = receivedCash(data.receivedByMethod)

  return tableReportPrintFor({
    company: input.company,
    template: input.template,
    title: 'Resumo do período',
    filters: [
      { label: 'Período', value: input.periodLabel },
      { label: 'Loja', value: input.branchLabel },
    ],
    summary: [
      {
        label: `Total vendido (${plural(sales.count, 'venda', 'vendas')})`,
        value: brl(sales.revenue),
        bold: true,
      },
      { label: 'Recebido (sem vale)', value: brl(cash.total), bold: true },
      {
        label: `Vendido no crediário (${plural(data.creditSales.count, 'venda', 'vendas')})`,
        value: brl(data.creditSales.amount),
      },
      { label: 'Descontos dados', value: brl(sales.discountTotal) },
      {
        label: `Canceladas (${plural(sales.cancelled.count, 'venda', 'vendas')})`,
        value: brl(sales.cancelled.amount),
      },
    ],
    sections: [
      {
        title: 'Recebido por forma de pagamento',
        columns: [
          { label: 'Forma' },
          { label: 'Vezes', align: 'right' as const },
          { label: 'Valor', align: 'right' as const },
        ],
        rows: data.receivedByMethod.map((method) => [
          method.kind === 'voucher'
            ? `${method.name} (não é dinheiro novo)`
            : method.name,
          String(method.count),
          brl(method.amount),
        ]),
        footer: ['Total sem vale', '', brl(cash.total)],
      },
      {
        title: 'Vendas por vendedor',
        columns: [
          { label: 'Vendedor' },
          { label: 'Vendas', align: 'right' as const },
          { label: 'Ticket médio', align: 'right' as const },
          { label: 'Vendido', align: 'right' as const },
        ],
        rows: data.bySalesperson.map((person) => [
          person.name ?? 'Sem vendedor',
          String(person.salesCount),
          brl(person.averageTicket),
          brl(person.revenue),
        ]),
        footer: ['Total', String(sales.count), '', brl(sales.revenue)],
      },
      {
        title: 'Trocas e vales',
        columns: [
          { label: 'O quê' },
          { label: 'Valor', align: 'right' as const },
        ],
        rows: [
          [
            `Devolvido em trocas (${plural(exchanges.completed, 'troca', 'trocas')})`,
            brl(exchanges.returnedAmount),
          ],
          [
            `Vale dado (${plural(exchanges.vouchersIssued.count, 'vale', 'vales')})`,
            brl(exchanges.vouchersIssued.amount),
          ],
          ['Vale usado para pagar', brl(exchanges.vouchersRedeemed)],
          [
            `Da gestão antiga: vale dado (${plural(exchanges.vouchersIssuedOld.count, 'vale', 'vales')})`,
            brl(exchanges.vouchersIssuedOld.amount),
          ],
          ['Da gestão antiga: vale usado', brl(exchanges.vouchersRedeemedOld)],
        ],
      },
    ],
    notes: [
      'Recebido é o que entrou no período, pelo dia em que entrou: o crediário entra quando o cliente paga, e o troco já saiu do dinheiro.',
      ...(input.branchChosen
        ? ['Os vales são da loja inteira, não só desta filial.']
        : []),
    ],
  })
}
